import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// This page has no Sharetribe Console-hosted asset (unlike Privacy Policy /
// Terms of Service, which are asset-driven with a fallback) -- it's a new
// page, so it always renders this committed content directly via
// PageBuilder, same rendering pipeline and styling as the other legal pages.
const cancellationPolicyMarkdown = `
*Last updated: August 2026*

*This policy describes how cancellations and refunds actually work on Coachify today. It is not a substitute for legal advice.*

This policy governs cancellations and refunds for sessions booked and paid for through Coachify Marketplace. A booking is an agreement between a Customer and a Provider; Coachify provides the cancellation and refund tools described below to help both sides handle changes fairly, but is not a party to the underlying booking -- see our [Terms of Service](/terms-of-service) for more.

## Cancelling 24 hours or more before your session

Either the Customer or the Provider can cancel a booking directly, any time up to 24 hours before the session start. The Customer receives a refund of the amount paid, **excluding Coachify's platform booking fee** (currently 8% of the session price, disclosed at checkout). If the session was booked with subscription credits instead of a card, the credit is returned to the Customer's account instead of a cash refund.

## Cancelling within 24 hours of your session

**If the Provider cancels:** a Provider can cancel a session that starts within 24 hours without needing the Customer's approval. The Customer is still refunded the session price, excluding the platform booking fee. Because late cancellations disrupt Customers' plans, we track a Provider's late cancellations, and repeated late cancellations may result in a cancellation fee and/or a temporary hold on the Provider's ability to accept new bookings -- Providers can see their current standing in their dashboard.

**If the Customer wants to cancel:** cancelling within 24 hours of the session requires the Provider's approval, since the Provider may have already blocked off that time. The Customer submits a cancellation request, and the Provider can accept or decline it:

- If the Provider **accepts**, the booking is cancelled. Refund eligibility for an approved within-24-hour cancellation is shown at the time you submit the request and isn't guaranteed to match the 24-hour-or-more refund above -- check the exact terms shown before you confirm.
- If the Provider **declines**, the booking remains confirmed and the session is expected to take place as scheduled.

## No-shows

If you don't attend a scheduled session and didn't cancel it in advance, no refund applies.

## The platform booking fee is non-refundable

Coachify's platform booking fee (currently 8% of the session price) is not refunded under any cancellation scenario where a refund applies -- it covers the cost of operating the booking, payment, and scheduling infrastructure regardless of whether a session ultimately takes place.

## How refunds are paid

Refunds for card payments are issued to your original payment method through Stripe and typically appear within 5-10 business days, depending on your bank. Subscription-credit bookings return the credit to your Coachify account balance instead.

## Repeated cancellations

Coachify may, at its discretion, apply cancellation fees, place a temporary hold on booking privileges, or suspend an account (Customer or Provider) that cancels repeatedly, consistent with our [Terms of Service](/terms-of-service).

## Questions or disputes

If you believe a cancellation or refund was handled incorrectly, contact support@coachifyusa.com with your booking details.
`;

const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'cancellation-policy',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Cancellation & Refund Policy' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: cancellationPolicyMarkdown,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Cancellation & Refund Policy | Coachify',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'How cancellations and refunds work for sessions booked through Coachify.',
    },
  },
};

const CancellationPolicyPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default CancellationPolicyPage;
